import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { formatApiError } from "../../lib/api";
import { ArrowRight } from "lucide-react";

export default function PortalLoginPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      nav("/portal/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-portal-login" className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-[#0F292F] text-white p-16 flex-col justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#CFA968] flex items-center justify-center">
            <span className="font-serif text-[#0F292F] text-2xl leading-none">H</span>
          </div>
          <div>
            <div className="font-serif text-xl">H. Elliot Associates</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Client Portal</div>
          </div>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#CFA968] mb-4">— Welcome back</div>
          <h1 className="font-serif text-5xl xl:text-6xl font-light leading-[1.05] mb-6">
            Your plan, <span className="italic text-[#CFA968]">in one place</span>.
          </h1>
          <p className="text-white/70 leading-relaxed max-w-md">
            Documents, messages, upcoming appointments, and the latest updates from your advisor —
            quietly secure, always accessible.
          </p>
        </div>
        <div className="text-xs text-white/40 leading-relaxed">
          Federally licensed Enrolled Agent (IRS) · Bcrypt-hashed credentials, JWT-secured sessions.
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16 bg-[#F9F8F6]">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">
            — {mode === "login" ? "Sign In" : "Create Account"}
          </div>
          <h2 className="font-serif text-4xl text-[#0F292F] font-light mb-8 leading-tight">
            {mode === "login" ? "Welcome back." : "Open your portal."}
          </h2>

          <form onSubmit={submit} data-testid={`auth-form-${mode}`} className="space-y-6">
            {mode === "register" && (
              <label className="block">
                <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Full Name</div>
                <input
                  data-testid="auth-name"
                  required
                  className="he-input"
                  value={form.name}
                  onChange={onChange("name")}
                />
              </label>
            )}
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Email</div>
              <input
                type="email"
                data-testid="auth-email"
                required
                className="he-input"
                value={form.email}
                onChange={onChange("email")}
              />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Password</div>
              <input
                type="password"
                data-testid="auth-password"
                required
                minLength={mode === "register" ? 8 : 1}
                className="he-input"
                value={form.password}
                onChange={onChange("password")}
              />
            </label>
            {error && (
              <div data-testid="auth-error" className="text-sm text-red-700 border-l-2 border-red-700 pl-3">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="auth-submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? "Please wait..." : (
                <>{mode === "login" ? "Sign In" : "Create Account"} <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#52525B]">
            {mode === "login" ? (
              <>New to the portal?{" "}
                <button
                  data-testid="auth-switch-register"
                  className="text-[#0F292F] underline"
                  onClick={() => { setMode("register"); setError(""); }}
                >
                  Create an account
                </button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button
                  data-testid="auth-switch-login"
                  className="text-[#0F292F] underline"
                  onClick={() => { setMode("login"); setError(""); }}
                >
                  Sign in
                </button>
              </>
            )}
          </div>

          <div className="mt-12 text-xs text-[#52525B]">
            <Link to="/" className="he-link">← Return to website</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
